import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Trash, Package, ArrowRight, Warning } from "@phosphor-icons/react";
import { myApi } from "../api";
import { gaEcommerce, cartToGaItems } from "@/lib/ga";
import { useCart } from "../contexts/CartContext";
import CartItemRow from "../components/shop/CartItemRow";
import ProductSearchBar from "../components/shop/ProductSearchBar";
import CartSummaryCard from "../components/shop/CartSummaryCard";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);

export default function MyCart() {
  const { refresh, loading: ctxLoading } = useCart();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);

  const reloadLocal = useCallback(async () => {
    try {
      setError("");
      const data = await myApi.cartGet();
      setCart(data || {});
    } catch (e) {
      setError(e.message || "Erro ao carregar o carrinho.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reloadLocal(); }, [reloadLocal]);

  const withAction = async (fn) => {
    setActing(true);
    try { await fn(); await reloadLocal(); await refresh(); }
    catch (e) { setError(e.message || "Operação falhou."); }
    finally { setActing(false); }
  };

  // API returns data inside 'obj' wrapper: {success, message, obj: {produtos, packs, total, ...}}
  const cartData = cart?.obj || cart || {};
  const produtos = cartData?.produtos || [];
  const packs = cartData?.packs || [];
  const currency = cartData?.ID_Moeda || "EUR";
  const total = cartData?.total ?? produtos.reduce((s, p) => s + (Number(p.subtotal) || 0), 0)
    + packs.reduce((s, p) => s + (Number(p.subtotal) || 0), 0);
  const blackMonth = cartData?.blackMonth || null;
  const bmDesconto = Number(blackMonth?.desconto || 0);
  const totalSemPromo = Number(cartData?.totalSemPromo ?? total);
  const isEmpty = produtos.length === 0 && packs.length === 0;

  const gaViewed = useRef(false);
  useEffect(() => {
    if (!gaViewed.current && !loading && !isEmpty) {
      gaViewed.current = true;
      gaEcommerce("view_cart", cartToGaItems(produtos, packs), total, currency);
    }
  }, [loading, isEmpty, produtos, packs, total, currency]);

  return (
    <div className="min-h-full bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="my-cart-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0F1829] flex items-center gap-3" style={{ fontFamily: "'Fraunces', sans-serif" }}>
              <ShoppingCart size={32} weight="fill" className="text-[#002480]" />
              Carrinho
            </h1>
            <p className="text-[#6B7280] text-sm mt-1">Adicione produtos e finalize a sua encomenda.</p>
          </div>
          {!isEmpty && (
            <button data-testid="cart-clear-btn" disabled={acting}
              onClick={() => { if (window.confirm("Limpar todo o carrinho?")) withAction(() => myApi.cartClear()); }}
              className="text-xs text-[#FF2B2B] hover:bg-red-50 px-3 py-2 rounded-md transition-colors flex items-center gap-1.5 disabled:opacity-50">
              <Trash size={14} /> Esvaziar carrinho
            </button>
          )}
        </div>

        {error && (
          <div data-testid="cart-error" className="mb-6 p-3 rounded-md bg-red-50 border border-red-200 text-[#FF2B2B] text-sm flex items-start gap-2">
            <Warning size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Search bar */}
        <ProductSearchBar onAdded={async () => { await reloadLocal(); await refresh(); }} disabled={acting} />

        {loading ? (
          <div className="mt-8 p-12 text-center text-[#6B7280] bg-white rounded-xl border border-[#E5E7EB]" data-testid="cart-loading">A carregar carrinho…</div>
        ) : isEmpty ? (
          <EmptyCart />
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items */}
            <div className="lg:col-span-2 space-y-3">
              {produtos.map((p, i) => (
                <CartItemRow key={`prod-${p.ID_Produto}-${i}`} item={p} currency={currency} disabled={acting}
                  onUpdate={(qty) => withAction(() => myApi.cartUpdateQty(p.ID_Produto, qty))}
                  onRemove={() => { gaEcommerce("remove_from_cart", cartToGaItems([p]), Number(p.subtotal) || 0, currency); withAction(() => myApi.cartRemove(p.ID_Produto)); }} />
              ))}
              {packs.map((p, i) => (
                <CartItemRow key={`pack-${p.ID_Pack}-${i}`} item={p} currency={currency} isPack disabled={acting}
                  onRemove={() => { gaEcommerce("remove_from_cart", cartToGaItems([], [p]), Number(p.subtotal) || 0, currency); withAction(() => myApi.cartRemovePack(p.ID_Pack)); }} />
              ))}
            </div>

            {/* Summary */}
            <CartSummaryCard total={total} currency={currency} fmt={fmt}
              grossTotal={totalSemPromo} discount={bmDesconto}
              discountLabel={blackMonth?.label}
              itemCount={produtos.length + packs.length} disabled={acting} />
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyCart() {
  return (
    <div className="mt-8 p-12 text-center bg-white rounded-xl border border-[#E5E7EB]" data-testid="cart-empty">
      <Package size={48} className="mx-auto text-[#9CA3AF] mb-4" />
      <h2 className="text-xl font-black tracking-tighter text-[#0F1829] mb-2" style={{ fontFamily: "'Fraunces', sans-serif" }}>
        O seu carrinho está vazio
      </h2>
      <p className="text-[#6B7280] text-sm mb-6">Use a barra acima para procurar e adicionar produtos.</p>
      <Link to="/my" className="inline-flex items-center gap-2 text-[#002480] hover:text-[#0F1829] text-sm font-medium transition-colors">
        Voltar à página inicial <ArrowRight size={14} />
      </Link>
    </div>
  );
}
